import moment from 'moment';

const STORAGE_KEYS = {
	TRIP_HISTORY: 'trip_history_date_range',
	TRIP_HISTORY_LIST: 'trip_history_list_date_range',
	INTRACITY: 'intracity_date_range',
	INTERCITY: 'intercity_date_range',
	ADHOC_REQUEST: 'adhoc_request_date_range',
	CLIENT_INVOICE: 'client_invoice_date_range',
	// New keys for filter persistence
	TRIP_HISTORY_FILTERS: 'trip_history_filters',
	INTRACITY_FILTERS: 'intracity_filters',
	INTERCITY_FILTERS: 'intercity_filters',
	ADHOC_REQUEST_FILTERS: 'adhoc_request_filters',
};

// Save date range to localStorage
export const saveDateRange = (pageKey, dateRange) => {
	try {
		const data = {
			from_date: dateRange?.from_date || null,
			to_date: dateRange?.to_date || null,
			from_date_moment: dateRange?.from_date_moment
				? dateRange.from_date_moment.toISOString()
				: null,
			to_date_moment: dateRange?.to_date_moment
				? dateRange.to_date_moment.toISOString()
				: null,
			timestamp: Date.now(),
		};
		localStorage.setItem(STORAGE_KEYS[pageKey], JSON.stringify(data));
	} catch (error) {
		console.error('Error saving date range to localStorage:', error);
	}
};

// Load date range from localStorage
export const loadDateRange = (pageKey) => {
	try {
		const stored = localStorage.getItem(STORAGE_KEYS[pageKey]);
		if (stored) {
			const data = JSON.parse(stored);
			// Check if data is not older than 24 hours
			const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
			if (isExpired) {
				localStorage.removeItem(STORAGE_KEYS[pageKey]);
				return null;
			}
			return data;
		}
	} catch (error) {
		console.error('Error loading date range from localStorage:', error);
	}
	return null;
};

// Clear date range from localStorage
export const clearDateRange = (pageKey) => {
	try {
		localStorage.removeItem(STORAGE_KEYS[pageKey]);
	} catch (error) {
		console.error('Error clearing date range from localStorage:', error);
	}
};

// Save filters to localStorage
export const saveFilters = (pageKey, filters) => {
	try {
		const data = {
			...filters,
			timestamp: Date.now(),
		};
		localStorage.setItem(STORAGE_KEYS[pageKey], JSON.stringify(data));
	} catch (error) {
		console.error('Error saving filters to localStorage:', error);
	}
};

// Load filters from localStorage
export const loadFilters = (pageKey) => {
	try {
		const stored = localStorage.getItem(STORAGE_KEYS[pageKey]);
		if (stored) {
			const data = JSON.parse(stored);
			// Check if data is not older than 24 hours
			const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
			if (isExpired) {
				localStorage.removeItem(STORAGE_KEYS[pageKey]);
				return null;
			}
			// Remove timestamp from returned data
			const { timestamp, ...filters } = data;
			return filters;
		}
	} catch (error) {
		console.error('Error loading filters from localStorage:', error);
	}
	return null;
};

// Clear filters from localStorage
export const clearFilters = (pageKey) => {
	try {
		localStorage.removeItem(STORAGE_KEYS[pageKey]);
	} catch (error) {
		console.error('Error clearing filters from localStorage:', error);
	}
};

// Save both date range and filters together
export const saveTripFilters = (pageKey, dateRange, filters) => {
	// Save date range
	if (dateRange) {
		saveDateRange(pageKey, dateRange);
	}

	// Save other filters
	if (filters) {
		const filterKey = `${pageKey}_FILTERS`;
		saveFilters(filterKey, filters);
	}
};

// Load both date range and filters together
export const loadTripFilters = (pageKey) => {
	const dateRange = loadDateRange(pageKey);
	const filterKey = `${pageKey}_FILTERS`;
	const filters = loadFilters(filterKey);

	return { dateRange, filters };
};

// Clear both date range and filters together
export const clearTripFilters = (pageKey) => {
	clearDateRange(pageKey);
	const filterKey = `${pageKey}_FILTERS`;
	clearFilters(filterKey);
};

// Convert moment dates to formatted strings for API
export const formatDateForAPI = (momentDate) => {
	if (!momentDate) return null;
	const formattedDate = moment.utc(momentDate).format('MM/DD/YYYY');
	const validData = `${formattedDate} 00:00`;
	return moment.utc(validData).subtract(330, 'minutes').format('MM/DD/YYYY HH:mm:ss');
};

export const formatEndDateForAPI = (momentDate) => {
	if (!momentDate) return null;
	const formattedDate = moment.utc(momentDate).format('MM/DD/YYYY');
	const validData = `${formattedDate} 23:59`;
	return moment.utc(validData).subtract(330, 'minutes').format('MM/DD/YYYY HH:mm:ss');
};

// Convert stored moment ISO strings back to moment objects for RangePicker
export const formatDateForRangePicker = (momentISOString) => {
	if (!momentISOString) return null;
	return moment(momentISOString);
};
